'use client';

import { useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { compareObjects } from '@/lib/Compare';
import { readVersionList } from '@/api/version/read';
import { createVersion, deleteVersion } from '@/api/version/create';
import { updateVersionDetail } from '@/api/version/update';
import { ReadVersionType } from '@/types/version/read';
import { CreateVersionType } from '@/types/version/create';
import { UpdateVersionType } from '@/types/version/update';
import Header from '@/component/Header';

export default function Version() {
  const getTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const [rows, setRows] = useState<ReadVersionType[]>([
    {
      versionId: getTempId(),
      name: '',
      version: '',
      description: '',
      regDt: '',
      modDt: '',
    },
  ]);
  const [oldRows, setOldRows] = useState<ReadVersionType[]>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const re = await readVersionList();
        if (re.data && re.data.length > 0) {
          const normalizedData = re.data.map((item: ReadVersionType) => ({
            ...item,
            name: item.name ?? '',
            version: item.version ?? '',
            description: item.description ?? '',
          }));
          setRows(normalizedData);
          setOldRows(normalizedData);
          setSelectedRows(new Set());
          setDeletedRows(new Set());
        } else {
          const initialRow = {
            versionId: getTempId(),
            name: '',
            version: '',
            description: '',
            regDt: '',
            modDt: '',
          };
          setRows([initialRow]);
          setOldRows([initialRow]);
          setSelectedRows(new Set());
          setDeletedRows(new Set());
        }
      } catch (error) {
        console.error('버전 목록 조회 실패:', error);
      }
    })();
  }, []);

  const handleAddInputs = () => {
    const tempId = getTempId();
    setRows([
      ...rows,
      {
        versionId: tempId,
        name: '',
        version: '',
        description: '',
        regDt: '',
        modDt: '',
      },
    ]);
  };

  const handleDelete = () => {
    if (selectedRows.size === 0) {
      toast.error('삭제할 항목을 선택해주세요.');
      return;
    }

    if (rows.length - selectedRows.size < 1) {
      toast.error('최소 1개의 행이 필요합니다.');
      return;
    }

    // 삭제 대기 목록에 추가 (화면에서만 제거)
    setDeletedRows((prev) => {
      const newSet = new Set(prev);
      selectedRows.forEach((id) => {
        // temp-로 시작하는 항목은 서버에 없으므로 삭제 대기 목록에 추가하지 않음
        if (!id.startsWith('temp-')) {
          newSet.add(id);
        }
      });
      return newSet;
    });

    // 선택된 행들을 rows에서 제거 (화면에서만)
    setRows((prevRows) => prevRows.filter((row) => !selectedRows.has(row.versionId)));
    setSelectedRows(new Set());
    toast.success('삭제할 항목이 선택되었습니다. 버전 업데이트 버튼을 눌러 저장하세요.');
  };

  const handleInputChange = (rowId: string, field: keyof ReadVersionType, value: string) => {
    if (!rowId || rowId === '') {
      console.error('rowId가 없습니다:', rowId);
      return;
    }

    setRows((prevRows) => {
      const targetRow = prevRows.find((row) => row.versionId === rowId);
      if (!targetRow) {
        console.error('해당 rowId를 가진 행을 찾을 수 없습니다:', rowId, prevRows);
        return prevRows;
      }

      return prevRows.map((row) => {
        if (row.versionId === rowId) {
          return { ...row, [field]: value };
        }
        return row;
      });
    });
  };

  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  };

  const handleUpdate = async (event: MouseEvent<HTMLButtonElement>) => {
    // 체크된 항목이 2개 이상이면 오류 처리
    if (selectedRows.size >= 2) {
      toast.error('버전 업데이트 시에는 최대 1개의 항목만 선택할 수 있습니다.');
      return;
    }

    console.log('버전 업데이트');
    console.log('현재 rows:', rows);
    console.log('oldRows:', oldRows);
    console.log('삭제 대기 목록:', deletedRows);

    try {
      // DELETE - 삭제 대기 목록의 항목들을 서버에서 삭제
      const deletePromises = Array.from(deletedRows).map((versionId) => deleteVersion(versionId));
      console.log('DELETE 대상:', Array.from(deletedRows));

      // CREATE - temp-로 시작하는 ID는 새로 생성
      const createRows = rows.filter((row) => row.versionId.startsWith('temp-'));
      console.log('CREATE 대상 행:', createRows);

      const createPromises = createRows.map(async (row) => {
        const createData: CreateVersionType = {
          name: row.name,
          version: row.version,
          description: row.description,
        };
        console.log('새 행 생성:', createData);
        return await createVersion(createData);
      });

      // UPDATE - 기존 행의 변경사항 업데이트 (삭제 대기 목록에 없는 항목만)
      const updatePromises = rows
        .map((row) => {
          if (row.versionId.startsWith('temp-')) {
            return null;
          }

          // 삭제 대기 목록에 있는 항목은 업데이트하지 않음
          if (deletedRows.has(row.versionId)) {
            return null;
          }

          const oldRow = oldRows.find((old) => old.versionId === row.versionId);
          if (!oldRow) {
            return null;
          }

          const changes = compareObjects(row, oldRow);

          if (Object.keys(changes).length > 0) {
            const updateData: UpdateVersionType = {
              versionId: row.versionId,
              ...changes,
            };
            console.log('업데이트 데이터:', updateData);
            return updateVersionDetail(updateData);
          }

          return null;
        })
        .filter((promise) => promise !== null) as Promise<any>[];

      // 모든 작업을 병렬로 실행
      await Promise.all([...deletePromises, ...createPromises, ...updatePromises]);
      console.log('모든 작업 완료');

      toast.success('저장이 완료되었습니다.');

      // 저장 후 서버에서 최신 데이터 다시 가져오기
      const re = await readVersionList();
      if (re.data && re.data.length > 0) {
        const normalizedData = re.data.map((item: ReadVersionType) => ({
          ...item,
          name: item.name ?? '',
          version: item.version ?? '',
          description: item.description ?? '',
        }));
        setRows(normalizedData);
        setOldRows(normalizedData);
        setSelectedRows(new Set());
        setDeletedRows(new Set());
      } else {
        const initialRow = {
          versionId: getTempId(),
          name: '',
          version: '',
          description: '',
          regDt: '',
          modDt: '',
        };
        setRows([initialRow]);
        setOldRows([initialRow]);
        setSelectedRows(new Set());
        setDeletedRows(new Set());
      }
    } catch (error) {
      console.error('저장 실패:', error);
      toast.error('저장에 실패했습니다.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flex: 1,
        flexDirection: 'column',
        gap: { xs: '16px', sm: '0px' },
      }}
    >
      <Card>
        <Header title="버전 관리" />
        <Divider />
        <CardContent
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          <Box p={2}>
            <Grid xs={12}>
              <Typography variant="h4">버전 세팅</Typography>
            </Grid>
            <Grid container spacing={2} mb={2}>
              <Grid xs={1} sm={1}>
                <Typography variant="h6">선택</Typography>
              </Grid>
              <Grid xs={3} sm={3}>
                <Typography variant="h6">이름(URL 주소)</Typography>
              </Grid>
              <Grid xs={3} sm={3}>
                <Typography variant="h6">버전</Typography>
              </Grid>
              <Grid xs={5} sm={5}>
                <Typography variant="h6">설명</Typography>
              </Grid>
            </Grid>

            {rows.map((row: ReadVersionType, index: number) => {
              const rowId = row.versionId;

              return (
                <Grid container spacing={2} key={rowId || `row-${index}`} mb={2}>
                  <Grid xs={1} sm={1} display="flex" alignItems="center" justifyContent="center">
                    <Checkbox
                      checked={selectedRows.has(rowId)}
                      onChange={(e) => handleCheckboxChange(rowId, e.target.checked)}
                    />
                  </Grid>
                  <Grid xs={3} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>이름</InputLabel>
                      <OutlinedInput
                        value={row.name ?? ''}
                        label="이름"
                        name="name"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleInputChange(row.versionId, 'name', e.target.value);
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid xs={3} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>버전</InputLabel>
                      <OutlinedInput
                        value={row.version ?? ''}
                        label="버전"
                        name="version"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleInputChange(row.versionId, 'version', e.target.value);
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid xs={5} sm={5}>
                    <FormControl fullWidth>
                      <InputLabel>설명</InputLabel>
                      <OutlinedInput
                        value={row.description ?? ''}
                        label="설명"
                        name="description"
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleInputChange(row.versionId, 'description', e.target.value);
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              );
            })}

            <Grid container spacing={2} mt={2}>
              <Grid xs={6}>
                <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
                  추가
                </Button>
              </Grid>
              <Grid xs={6}>
                <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
                  삭제
                </Button>
              </Grid>
              <Grid xs={12}>
                <Button fullWidth variant="contained" color="primary" onClick={handleUpdate}>
                  버전 업데이트
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
